import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule, NgComponentOutlet } from '@angular/common';
import { animate, style, transition, trigger } from '@angular/animations';
import { DomSanitizer } from '@angular/platform-browser';
import { DialogStackService } from '@/share/services/dialog-stack.service';
import { ActionButtonVariant, DialogLayer, ResolvedButton } from '@/share/model/dialog.types';
import { actionButtonClass } from '@/share/functions/action-button-class';
import { TranslatePipe } from '@ngx-translate/core';
import { CanAction } from '@/core/directive/can-action';

@Component({
    selector: 'app-dynamic-dialog',
    standalone: true,
    imports: [CommonModule, NgComponentOutlet, TranslatePipe, CanAction],
    animations: [
        trigger('backdropAnim', [transition(':enter', [style({ opacity: 0 }), animate('180ms ease-out', style({ opacity: 1 }))]), transition(':leave', [animate('150ms ease-in', style({ opacity: 0 }))])]),
        trigger('panelAnim', [
            transition(':enter', [style({ opacity: 0, transform: 'translateY(24px) scale(0.98)' }), animate('220ms cubic-bezier(0.4,0,0.2,1)', style({ opacity: 1, transform: 'translateY(0) scale(1)' }))]),
            transition(':leave', [animate('160ms cubic-bezier(0.4,0,0.2,1)', style({ opacity: 0, transform: 'translateY(16px) scale(0.99)' }))])
        ]),
        trigger('panelAnimRight', [
            transition(':enter', [style({ transform: 'translateX(100%)' }), animate('220ms cubic-bezier(0.4,0,0.2,1)', style({ transform: 'translateX(0)' }))]),
            transition(':leave', [animate('180ms cubic-bezier(0.4,0,0.2,1)', style({ transform: 'translateX(100%)' }))])
        ])
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    template: `
        @for (layer of stack.stack(); track layer.id) {
            @if (layer.depth === 0) {
                <!-- Backdrop -->
                <div @backdropAnim class="fixed inset-0 bg-black/30 backdrop-blur-[3px]" [style.z-index]="layer.zIndex - 1" (click)="onBackdropClick(layer)" aria-hidden="true"></div>

                <!-- Positioner -->
                <div
                    class="fixed inset-0 flex items-start pointer-events-none"
                    [style.z-index]="layer.zIndex"
                    [class.justify-end]="layer.config.position === 'right'"
                    [class.justify-center]="layer.config.position !== 'right'"
                    [class.pt-0]="layer.maximized || layer.config.position === 'right'"
                    [class.pt-10]="!layer.maximized && layer.config.position !== 'right'"
                    [class.px-0]="layer.maximized || layer.config.position === 'right'"
                    [class.px-10]="!layer.maximized && layer.config.position !== 'right'"
                >
                    <!-- Panel row -->
                    <div
                        [@panelAnim]="layer.config.position !== 'right' ? true : undefined"
                        [@panelAnimRight]="layer.config.position === 'right' ? true : undefined"
                        class="flex items-start gap-2 pointer-events-none"
                        [style.width]="layer.maximized ? '100vw' : (layer.config.width ?? '80vw')"
                        [style.height]="layer.config.position === 'right' ? '100vh' : (layer.maximized ? '100vh' : (layer.config.height ?? '88vh'))"
                    >
                        <!-- White card -->
                        <div
                            class="flex flex-col flex-1 min-w-0 h-full bg-white shadow-2xl border border-slate-200 overflow-hidden pointer-events-auto"
                            [class.rounded-2xl]="layer.config.position !== 'right'"
                        >
                            <ng-container *ngTemplateOutlet="mainHeader; context: { layer: layer }" />

                            <div class="flex-1 overflow-y-auto px-2">
                                <ng-container *ngComponentOutlet="layer.config.component!; inputs: { dialogData: layer.config.data }" />
                            </div>

                            @if (layer.config.position !== 'right') {
                                <!-- Footer help icon -->
                                <div class="flex justify-end px-5 py-2 flex-shrink-0">
                                    <button type="button" class="text-slate-300 hover:text-slate-400 transition-colors" title="Help">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                                            <circle cx="12" cy="12" r="10" />
                                            <path d="M12 16v-4M12 8h.01" />
                                        </svg>
                                    </button>
                                </div>
                            }
                        </div>

                        @if (layer.actions.length) {
                            <!-- Side action buttons — data-layer-id goes here -->
                            <div class="flex flex-col items-center h-full">
                                <div class="flex-1 flex flex-col gap-2 pt-0.5 pointer-events-auto" [attr.data-layer-id]="layer.id">
                                    <ng-container *ngTemplateOutlet="buttonList; context: { layer: layer, iconOnly: !layer.showLabel }" />
                                </div>
                                <!-- Label toggle -->
                                <div class="pointer-events-auto">
                                    <button type="button" (click)="stack.toggleLabel(layer.id)" class="h-10 w-10 flex items-center justify-center cursor-pointer">
                                        <ng-container *ngTemplateOutlet="layer.showLabel ? chevronLeft : chevronRight" />
                                    </button>
                                </div>
                            </div>
                        }
                    </div>
                </div>
            } @else {
                <!-- Backdrop (lighter) -->
                <div @backdropAnim class="fixed inset-0 bg-black/20" [style.z-index]="layer.zIndex - 1" (click)="onBackdropClick(layer)" aria-hidden="true"></div>

                <!-- Positioner -->
                <div class="fixed inset-0 flex items-center justify-center pointer-events-none" [style.z-index]="layer.zIndex">
                    <!-- Panel row with cascade offset -->
                    <div
                        @panelAnim
                        class="flex items-start gap-2 pointer-events-none"
                        [style.width]="layer.maximized ? '100vw' : (layer.config.width ?? '80vw')"
                        [style.height]="layer.maximized ? '100vh' : (layer.config.height ?? '88vh')"
                        [style.transform]="cascadeOffset(layer.depth)"
                    >
                        <!-- Modal card -->
                        <div class="flex flex-col flex-1 min-w-0 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden pointer-events-auto" [style.height]="resolveHeight(layer.config.height)">
                            <ng-container *ngTemplateOutlet="subHeader; context: { layer: layer }" />

                            <div class="flex-1 overflow-y-auto px-2">
                                <ng-container *ngComponentOutlet="layer.config.component!; inputs: { dialogData: layer.config.data }" />
                            </div>
                        </div>

                        <!-- Side action buttons — data-layer-id goes here -->
                        <div class="flex flex-col items-center h-full">
                            <div class="flex-1 flex flex-col gap-2 pt-0.5 pointer-events-auto" [attr.data-layer-id]="layer.id">
                                <ng-container *ngTemplateOutlet="buttonList; context: { layer: layer, iconOnly: !layer.showLabel }" />
                            </div>
                            <!-- Label toggle -->
                            <div class="pointer-events-auto">
                                <button type="button" (click)="stack.toggleLabel(layer.id)" class="h-10 w-10 flex items-center justify-center cursor-pointer">
                                    <ng-container *ngTemplateOutlet="layer.showLabel ? chevronLeft : chevronRight" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            }
        }

        <!-- Main dialog header -->
        <ng-template #mainHeader let-layer="layer">
            <div class="flex items-center justify-between px-6 pt-5 pb-4 border-b border-slate-200 flex-shrink-0">
                <h2 class="text-base font-semibold tracking-tight">{{ layer.config.header }}</h2>
                <div class="flex items-center gap-1">
                    @if (layer.config.maximizable !== false) {
                        <button
                            type="button"
                            (click)="stack.toggleMaximize(layer.id)"
                            class="cursor-pointer flex items-center justify-center w-7 h-7 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors duration-150"
                            [title]="layer.maximized ? 'Restore' : 'Maximize'"
                        >
                            @if (!layer.maximized) {
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                    <polyline points="15 3 21 3 21 9" />
                                    <polyline points="9 21 3 21 3 15" />
                                    <line x1="21" y1="3" x2="14" y2="10" />
                                    <line x1="3" y1="21" x2="10" y2="14" />
                                </svg>
                            } @else {
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                    <polyline points="4 14 10 14 10 20" />
                                    <polyline points="20 10 14 10 14 4" />
                                    <line x1="10" y1="14" x2="3" y2="21" />
                                    <line x1="21" y1="3" x2="14" y2="10" />
                                </svg>
                            }
                        </button>
                    }
                    <button
                        type="button"
                        (click)="stack.close(layer.id, null)"
                        class="cursor-pointer flex items-center justify-center w-7 h-7 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors duration-150"
                        title="Close"
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>
            </div>
        </ng-template>

        <!-- Sub-modal header -->
        <ng-template #subHeader let-layer="layer">
            <div class="flex items-center justify-between px-6 pt-5 pb-4 border-b border-slate-200 flex-shrink-0">
                <div class="flex flex-col gap-1">
                    <h2 class="text-base font-semibold tracking-tight">{{ layer.config.header }}</h2>
                    @if (layer.depth > 1) {
                        <div class="flex items-center gap-1">
                            @for (d of stack.depthArray(layer.depth); track d) {
                                <div class="w-1.5 h-1.5 rounded-full transition-colors" [class.bg-indigo-500]="d === layer.depth - 1" [class.bg-slate-300]="d !== layer.depth - 1"></div>
                            }
                        </div>
                    }
                </div>
                <button
                    type="button"
                    (click)="stack.close(layer.id, null)"
                    class="cursor-pointer flex items-center justify-center w-7 h-7 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors duration-150"
                    title="Close"
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            </div>
        </ng-template>

        <!-- Reusable button list -->
        <ng-template #buttonList let-layer="layer" let-iconOnly="iconOnly">
            @for (btn of layer.actions; track btn.action) {
                @if (btn.separator) {
                    <div class="w-[80%] self-center h-px bg-slate-300/70 my-0.5"></div>
                }
                <ng-container *appCanAction="btn.action">
                    <button type="button" [title]="btn.label | translate" [disabled]="btn.disabled || btn.loading" [class]="btnClass(btn.variant, !!btn.disabled, !!btn.loading, !iconOnly)" (click)="onButtonClick(layer, btn)">
                        @if (btn.loading) {
                            <svg class="animate-spin flex-shrink-0" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
                                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                            </svg>
                        } @else if (!iconOnly) {
                            <span class="flex items-center justify-center gap-2 w-full">
                                <span [innerHTML]="sanitizer.bypassSecurityTrustHtml(btn.safeIcon)" class="flex items-center justify-center flex-shrink-0 w-4 h-4"></span>
                                <span class="text-sm font-medium leading-none">{{ btn.label | translate }}</span>
                            </span>
                        } @else {
                            <span [innerHTML]="sanitizer.bypassSecurityTrustHtml(btn.safeIcon)" class="flex items-center justify-center w-4 h-4"></span>
                        }
                    </button>
                </ng-container>
            }
        </ng-template>

        <!-- Chevron icons -->
        <ng-template #chevronRight>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                    d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                    stroke="var(--color-secondary-200)"
                    stroke-width="1.5"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path d="M10.7383 15.5302L14.2583 12.0002L10.7383 8.47021" stroke="var(--color-secondary-200)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
        </ng-template>
        <ng-template #chevronLeft>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#FAFAFA" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M13.26 15.5297L9.73999 11.9997L13.26 8.46973" stroke="#FAFAFA" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
        </ng-template>
    `
})
export class DynamicDialog {
    readonly stack = inject(DialogStackService);
    readonly sanitizer = inject(DomSanitizer);

    onButtonClick(layer: DialogLayer, btn: ResolvedButton): void {
        if (!btn.action || btn.disabled || btn.loading) return;
        layer.bridge.dispatch(btn.action);
    }

    onBackdropClick(layer: DialogLayer): void {
        this.stack.close(layer.id, null);
    }

    cascadeOffset(depth: number): string {
        const px = (depth - 1) * 24;
        return `translate(${px}px, ${px}px)`;
    }

    resolveHeight(height?: string): string {
        return !height || height === 'auto' ? '85vh' : height;
    }

    btnClass(variant: ActionButtonVariant = 'secondary', disabled = false, loading = false, showLabel = false): string {
        return actionButtonClass({ variant, disabled, loading, showLabel, cursorStyle: 'toggle' });
    }
}
